import type { CustomObjectField } from '@kizen/api/custom-object';
import type { SectionConfig } from '@kizen/permissions/types';
import type { ExpandState } from '__components/Containers/Expander';
import type { CustomObjectSectionData } from './types';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { getStreamKey } from '@kizen/permissions/utils';
import { useGetCustomObjectConfigs } from '@kizen/permissions/hooks/useAddCustomObjectPermissions';
import { useGetFieldConfigs } from '@kizen/permissions/hooks/useAddFieldPermissions';
import { useAddPermissionsToSection } from '@kizen/permissions/hooks/useAddPermissionsToSection';
import { useAreFieldsLoaded } from '@kizen/permissions/hooks/useAreFieldsLoaded';
import { usePermissionValue } from '@kizen/permissions/hooks/usePermissionValue';
import { usePermissionsContext } from '@kizen/permissions/hooks/context';
import { useRemoveSectionPermissions } from '@kizen/permissions/hooks/useRemoveSectionPermissions';
import {
  allCustomObjectFieldsQuery,
  customObjectCategoriesQuery,
} from 'query/custom-object';
import {
  allPipelineObjectFieldsQuery,
  pipelineObjectCategoriesQuery,
} from 'query/pipeline';
import { snakeToCamelCaseKeys } from '__services/helpers';
import {
  CategoryLabel,
  FieldPermissionRows,
  PermissionRow,
  Section,
  SectionContent,
  SetAllRow,
} from './components';
import { formatString } from './util';

type Field = SnakeToCamelCase<CustomObjectField>;

type RowWithRuleTooltipProps = {
  affordance: 'checkbox' | 'range';
  label: string;
  labelIndent?: number;
  permission: string;
  section: string;
  objectName: string;
  tooltip?: string;
};

type CustomObjectSectionProps = {
  section: SectionConfig;
  showArrowBtn?: boolean;
  initialExpanderState?: ExpandState;
  showSwitchToggle?: boolean;
  disableMargin?: boolean;
  enableRelatedFieldRule?: boolean;
};

const DEFAULT_FIELDS = 'default_fields';
const defaultFieldsOrder: Record<string, number> = {
  name: 0,
  created: 1,
  stage: 2,
  owner: 3,
  entity_value: 4,
  estimated_close_date: 5,
  actual_close_date: 6,
  percentage_chance_to_close_low: 7,
  percentage_chance_to_close_high: 8,
};

const RowWithRuleTooltip = ({
  affordance,
  label,
  labelIndent,
  permission,
  section,
  tooltip,
  objectName,
}: RowWithRuleTooltipProps) => {
  const ruleTooltip = usePermissionValue(
    getStreamKey(permission, section),
    ({ tooltip }) => formatString(tooltip, objectName)
  );

  return (
    <PermissionRow
      section={section}
      permission={permission}
      affordance={affordance}
      tooltip={ruleTooltip || tooltip}
      label={label}
      labelIndent={labelIndent}
    />
  );
};

export const CustomObjectSection = ({
  section,
  showArrowBtn,
  initialExpanderState,
  showSwitchToggle,
  enableRelatedFieldRule = true,
  disableMargin,
}: CustomObjectSectionProps) => {
  const {
    key,
    label: section_label,
    metadata: { object_type, object_name } = { object_name: '' },
  } = section;
  const qc = useQueryClient();
  const { t } = useTranslation();
  const [hasPrefetched, setHasPrefetched] = useState(false);
  const previousCategoryHadLabels = useRef(false);
  const ctx = usePermissionsContext();
  const {
    meta_config: {
      custom_objects,
      custom_object_categories,
      default_access_labels,
    },
  } = ctx;
  const isSectionEnabled = usePermissionValue(key, ({ value }) => value);
  const areFieldsLoaded = useAreFieldsLoaded(key);
  const removeSectionPermissions = useRemoveSectionPermissions();
  const getCustomObjectConfigs = useGetCustomObjectConfigs();
  const getFieldConfigs = useGetFieldConfigs(enableRelatedFieldRule);
  const addPermissionsToSection = useAddPermissionsToSection();

  const sectionLabels = section.access_labels ?? default_access_labels;
  const isPipeline = object_type === 'pipeline';
  const enabled = Boolean(isSectionEnabled && areFieldsLoaded);

  const categorizedFields = useQueries({
    queries: [
      customObjectCategoriesQuery(key, {
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        enabled: enabled && !isPipeline,
      }),
      allCustomObjectFieldsQuery(key, {
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        enabled: enabled && !isPipeline,
      }),
      pipelineObjectCategoriesQuery(key, {
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        enabled: enabled && isPipeline,
      }),
      allPipelineObjectFieldsQuery(key, {
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        enabled: enabled && isPipeline,
      }),
    ],
    combine: ([
      co_categories,
      co_fields,
      pipeline_categories,
      pipeline_fields,
    ]) => {
      const categories = isPipeline
        ? pipeline_categories.data
        : co_categories.data;

      const fields = isPipeline ? pipeline_fields.data : co_fields.data;

      if (!categories || !fields) {
        return [];
      }

      const categorized = fields.reduce<Record<string, CustomObjectField[]>>(
        (acc, field) => {
          acc[field.category] = acc[field.category] || [];
          acc[field.category].push(field);
          return acc;
        },
        {}
      );

      const categories_with_fields = categories.map((category) => {
        return { ...category, fields: categorized[category.id] ?? [] };
      });

      return snakeToCamelCaseKeys(categories_with_fields);
    },
  });

  const addingPermissions = useRef(false);

  const withDefaultFieldsCategory = useMemo(() => {
    if (!categorizedFields) return null;
    const defaultCategoryIndex = categorizedFields?.findIndex((cat) =>
      cat.fields.some((x) => x.isDefault)
    );

    if (defaultCategoryIndex < 0) {
      return categorizedFields;
    }

    const defaultCategory = categorizedFields[defaultCategoryIndex];
    const init: [
      { id: string; name: string; fields: Field[] },
      { id: string; name: string; fields: Field[] },
    ] = [
      { id: DEFAULT_FIELDS, name: t('Default Fields'), fields: [] },
      { id: defaultCategory.id, name: defaultCategory.name, fields: [] },
    ];
    const defaultCategories = categorizedFields[
      defaultCategoryIndex
    ].fields.reduce((acc, field) => {
      if (field.isDefault) acc[0].fields.push(field);
      else acc[1].fields.push(field);
      return acc;
    }, init);
    const sortedDefaultCategory = {
      ...defaultCategories[0],
      fields: defaultCategories[0].fields.sort((a, b) => {
        return defaultFieldsOrder[a.name] - defaultFieldsOrder[b.name];
      }),
    };
    return [sortedDefaultCategory]
      .concat(defaultCategories[1])
      .concat(
        categorizedFields
          .slice(0, defaultCategoryIndex)
          .concat(
            categorizedFields.slice(
              defaultCategoryIndex + 1,
              categorizedFields.length
            )
          )
      );
  }, [categorizedFields, t]);

  const allFieldPermissionKeys = useMemo(() => {
    return (
      categorizedFields?.reduce<string[]>((acc, { fields }) => {
        acc.push(...fields.map(({ name }) => getStreamKey(name, key)));
        return acc;
      }, []) ?? []
    );
  }, [key, categorizedFields]);

  const { main, bulk, bulk_keys, default_for_new_field } = useMemo(() => {
    if (!isSectionEnabled || !areFieldsLoaded)
      return {
        main: [],
        bulk: [],
        bulk_keys: [],
        default_for_new_field: null,
      };
    return custom_objects.reduce<CustomObjectSectionData>(
      (acc, permission) => {
        if (permission.key === 'default_for_new_field') {
          acc.default_for_new_field = permission;
        } else if (permission.is_bulk_action) {
          acc.bulk.push(permission);
          acc.bulk_keys.push(getStreamKey(permission.key, key));
        } else {
          if (
            permission.category &&
            !acc.main.find(({ key }) => key === permission.category)
          ) {
            const customObjectsCategory = custom_object_categories.find(
              ({ key }) => key === permission.category
            );
            customObjectsCategory &&
              acc.main.push({ ...customObjectsCategory, isCategory: true });
          }
          acc.main.push(permission);
        }
        return acc;
      },
      { main: [], bulk: [], bulk_keys: [], default_for_new_field: null }
    );
  }, [
    isSectionEnabled,
    areFieldsLoaded,
    custom_objects,
    custom_object_categories,
    key,
  ]);

  const addPermissionsToContext = useCallback(async () => {
    if (!areFieldsLoaded && !addingPermissions.current) {
      addingPermissions.current = true;

      const fields = isPipeline
        ? await qc.fetchQuery(allPipelineObjectFieldsQuery(key))
        : await qc.fetchQuery(allCustomObjectFieldsQuery(key));

      const staticPermissions = getCustomObjectConfigs(key);
      const fieldPermissions = getFieldConfigs(
        snakeToCamelCaseKeys(fields),
        key
      );
      addPermissionsToSection(
        key,
        [...staticPermissions, ...fieldPermissions],
        { containsFieldPermissions: true }
      );
      addingPermissions.current = false;
    }
  }, [
    key,
    isPipeline,
    areFieldsLoaded,
    getCustomObjectConfigs,
    addPermissionsToSection,
    getFieldConfigs,
    qc,
  ]);

  const onSectionToggle = useCallback(
    (value: boolean) => {
      if (!value) {
        removeSectionPermissions(key);
      } else {
        addPermissionsToContext();
      }
    },
    [key, removeSectionPermissions, addPermissionsToContext]
  );

  const handleExpanderHover = useCallback(() => {
    if (!hasPrefetched && !areFieldsLoaded) {
      if (isPipeline) {
        qc.prefetchQuery(pipelineObjectCategoriesQuery(key));
        qc.prefetchQuery(allPipelineObjectFieldsQuery(key));
      } else {
        qc.prefetchQuery(customObjectCategoriesQuery(key));
        qc.prefetchQuery(allCustomObjectFieldsQuery(key));
      }

      setHasPrefetched(true);
    }
  }, [hasPrefetched, areFieldsLoaded, key, isPipeline, qc]);

  useEffect(() => {
    if (initialExpanderState === 'open' && !areFieldsLoaded) {
      addPermissionsToContext();
    }
  }, [initialExpanderState, areFieldsLoaded, addPermissionsToContext]);

  return (
    <Section
      title={section_label}
      sectionId={key}
      onChange={onSectionToggle}
      onExpand={addPermissionsToContext}
      onExpanderHover={handleExpanderHover}
      loading={!areFieldsLoaded}
      showArrowBtn={showArrowBtn}
      initialExpanderState={initialExpanderState}
      showSwitchToggle={showSwitchToggle}
      disableMargin={disableMargin}
    >
      {isSectionEnabled && (
        <SectionContent labels={sectionLabels}>
          {main.map((data) => {
            if ('isCategory' in data) {
              const {
                key: permission_key,
                label,
                tooltip,
                access_labels,
              } = data;

              let categoryLabels = access_labels;

              if (previousCategoryHadLabels.current && !categoryLabels) {
                categoryLabels = sectionLabels;
              }

              previousCategoryHadLabels.current = Boolean(categoryLabels);

              return (
                <CategoryLabel
                  key={permission_key}
                  marginBottom={label ? null : "17"}
                  accessLabels={categoryLabels}
                  label={label}
                  tooltip={tooltip}
                />
              );
            }

            const {
              affordance,
              key: permission_key,
              label,
              tooltip,
              category,
            } = data;

            return (
              <RowWithRuleTooltip
                key={permission_key}
                section={key}
                permission={permission_key}
                affordance={affordance}
                label={formatString(label, object_name)}
                objectName={object_name}
                labelIndent={category ? 40 : 0}
                tooltip={formatString(tooltip, object_name)}
              />
            );
          })}
          <SetAllRow
            showNone
            showRemove
            label={t('Perform Bulk Actions (Set All)')}
            permissionKeys={bulk_keys}
          />
          {bulk.map(({ affordance, key: permission_key, label, tooltip }) => {
            return (
              <RowWithRuleTooltip
                key={permission_key}
                section={key}
                permission={permission_key}
                affordance={affordance}
                label={label}
                tooltip={formatString(tooltip, object_name)}
                objectName={object_name}
                labelIndent={40}
              />
            );
          })}
          {default_for_new_field && (
            <RowWithRuleTooltip
              key={default_for_new_field.key}
              section={key}
              permission={default_for_new_field.key}
              affordance={default_for_new_field.affordance}
              label={default_for_new_field.label}
              objectName={object_name}
            />
          )}
          <SetAllRow
            showNone
            showView
            showEdit
            label={t('Individual {{ObjectName}} Field Permission (Set All)', {
              ObjectName: object_name,
            })}
            permissionKeys={allFieldPermissionKeys}
          />
          {areFieldsLoaded &&
            withDefaultFieldsCategory &&
            withDefaultFieldsCategory.map(({ id, fields, name }) => {
              return (
                <FieldPermissionRows
                  key={id}
                  sectionId={key}
                  categoryName={name}
                  fields={fields}
                  enableRelatedFieldRule={enableRelatedFieldRule}
                />
              );
            })}
        </SectionContent>
      )}
    </Section>
  );
};
